import { For, Show } from "solid-js";

import type { BriefResponse } from "~/lib/types";

type Props = {
  subjectLabel: string;
  pending: boolean;
  error: string | null;
  result: BriefResponse | null;
  onClose: () => void;
  onRegenerate: () => void;
};

/**
 * Generic brief modal — used by Account, Contact, Deal detail pages.
 * Renders the prose, error state, or loading. Footer reflects whatever
 * `context_counts` keys came back, so the same modal works for any
 * entity type without per-entity wiring.
 */
export function BriefModal(props: Props) {
  return (
    <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="brief-title">
      <div class="modal-box max-w-2xl">
        <h3 id="brief-title" class="font-bold text-lg mb-2">
          ✨ Brief for {props.subjectLabel}
        </h3>

        <Show when={props.pending}>
          <p role="status" aria-live="polite" class="text-sm opacity-60 py-6">
            Generating…
          </p>
        </Show>

        <Show when={props.error}>
          <div role="alert" class="alert alert-error text-sm">
            {props.error}
          </div>
        </Show>

        <Show when={props.result}>
          {(r) => (
            <>
              <pre class="whitespace-pre-wrap text-sm font-sans leading-relaxed">{r().brief}</pre>
              <div class="text-xs opacity-50 mt-4 flex flex-wrap gap-x-3 gap-y-1">
                <span>
                  model: <code>{r().model}</code>
                </span>
                <span>
                  tokens: {r().input_tokens ?? "?"} in / {r().output_tokens ?? "?"} out
                </span>
                <Show when={Object.keys(r().context_counts).length > 0}>
                  <span>
                    sourced{" "}
                    <For
                      each={Object.entries(r().context_counts).filter(([, v]) => (v as number) > 0)}
                    >
                      {([key, value], idx) => (
                        <>
                          <Show when={idx() > 0}> · </Show>
                          {value as number} {key.replace(/_/g, " ")}
                        </>
                      )}
                    </For>
                  </span>
                </Show>
              </div>
            </>
          )}
        </Show>

        <div class="modal-action">
          <button type="button" class="btn" onClick={props.onClose}>
            Close
          </button>
          <Show when={props.result && !props.pending}>
            <button type="button" class="btn btn-ghost" onClick={props.onRegenerate}>
              Regenerate
            </button>
          </Show>
        </div>
      </div>
    </dialog>
  );
}
